import React, { useState, useEffect, useRef } from "react";
import CommonButton from "../CommonButton";
import TabPills from "./TabPills";
import TextInputComp from "./TextInputComp";
import { Upload, ChevronDown, Check } from "lucide-react";

interface Option {
    id: string;
    label: string;
    image?: string;
    subtitle?: string;
    value?: string;
    url?: string;
}

export type QuestionType =
    | "options"
    | "text"
    | "dropdown"
    | "radio"
    | "image";

interface QuestionCardProps {
    currentStep: number;
    totalSteps: number;
    question: string;
    type: QuestionType;
    options?: Option[];
    value?: any;
    onChange?: (value: any) => void;
    onContinue?: () => void;
    onBack?: () => void;
    disabled?: boolean;
}

const QuestionCard: React.FC<QuestionCardProps> = ({
    currentStep,
    totalSteps,
    question,
    options = [],
    value,
    onChange,
    type,
    onContinue,
    onBack,
    disabled = false
}) => {
    const [preview, setPreview] = useState<string>("");
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState<string>("");
    const dropdownRef = useRef<HTMLDivElement>(null);

    const progress = (currentStep / totalSteps) * 100;

    // Filter options based on user typing
    const filteredOptions = (options || []).filter(
        (item: any) =>
            !item.label?.toLowerCase().includes("add custom breed") &&
            !item.id?.toLowerCase().includes("add custom breed")
    );

    const searchableOptions = filteredOptions.filter(item =>
        item.label?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    // Keep input search term updated with selected value
    useEffect(() => {
        if (type === "dropdown" && typeof value === "string") {
            setSearchTerm(value);
        }
    }, [value, type]);

    // Close dropdown on click outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsDropdownOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const isAnswered = () => {
        if (type === "image") {
            return value instanceof File || (typeof value === "string" && value.length > 0) || true;
        }

        if (type === "text" || type === "dropdown") {
            return typeof value === "string" && value.trim().length > 0;
        }

        if (type === "radio" || type === "options") {
            return value !== undefined && value !== null && value !== "";
        }

        return false;
    };

    const handleFileChange = (file: File) => {
        if (!file) return;
        setPreview(URL.createObjectURL(file));
        onChange?.(file);
    };

    const handleSearchInput = (e: React.ChangeEvent<HTMLInputElement>) => {
        const text = e.target.value;
        setSearchTerm(text);
        onChange?.(text); // Stores typed string directly
        setIsDropdownOpen(true);
    };

    const handleSelectOption = (selectedLabel: string) => {
        setSearchTerm(selectedLabel);
        onChange?.(selectedLabel);
        setIsDropdownOpen(false);
    };

    return (
        <div className="max-w-5xl mx-auto bg-white rounded-3xl shadow-md p-4 overflow-visible relative">
            {/* Progress Bar */}
            <div className="h-4 bg-orange-100 rounded-full overflow-hidden">
                <div
                    className="h-full bg-orange-500 transition-all duration-500"
                    style={{ width: `${progress}%` }}
                />
            </div>

            {/* Question Heading */}
            <h2 className="text-center text-xl font-bold text-logoHeading py-4">
                {question}
            </h2>

            {/* Content Container */}
            <div className="mt-4 pb-2 overflow-visible relative">
                
                {/* TYPE: OPTIONS */}
                {type === "options" && (
                    <div className="flex flex-wrap justify-center gap-6">
                        {filteredOptions.map((item) => (
                            <div key={item.id} className="flex flex-col items-center">
                                <button
                                    type="button"
                                    onClick={() => onChange?.(item.id)}
                                    className={`w-32 h-32 rounded-2xl overflow-hidden border-2 transition-all ${
                                        value === item.id
                                            ? "border-logoHeading ring-4 ring-green-100"
                                            : "border-gray-200"
                                    }`}
                                >
                                    <img
                                        src={item.image || item.url}
                                        alt={item.label}
                                        className="w-full h-full object-cover"
                                    />
                                </button>
                                <p className={`mt-2 font-medium ${value === item.id ? "text-logoHeading" : "text-gray-600"}`}>
                                    {item.label}
                                </p>
                            </div>
                        ))}
                    </div>
                )}

                {/* TYPE: IMAGE */}
                {type === "image" && (
                    <div className="flex flex-col items-center gap-6 py-2">
                        <div className="w-full">
                            <p className="text-xs font-semibold text-gray-500 text-center mb-4">
                                Choose a default avatar
                            </p>
                            
                            <div className="flex flex-wrap items-center justify-center gap-5 sm:gap-8">
                                {filteredOptions.map((avatar, idx) => {
                                    const avatarUrl = avatar.value || avatar.url || avatar.image || "";
                                    const isSelected = value === avatarUrl && !preview;

                                    return (
                                        <div key={avatar.id || idx} className="flex flex-col items-center space-y-1.5">
                                            <button
                                                type="button"
                                                onClick={() => {
                                                    setPreview("");
                                                    onChange?.(avatarUrl);
                                                }}
                                                className={`w-24 h-24 sm:w-28 sm:h-28 rounded-full border-2 overflow-hidden transition-all duration-300 bg-gray-50 flex items-center justify-center cursor-pointer ${
                                                    isSelected
                                                        ? "border-mainBrandColor ring-4 ring-orange-100 scale-105 shadow-md"
                                                        : "border-gray-200 hover:border-gray-400 hover:scale-102"
                                                }`}
                                            >
                                                <img
                                                    src={avatarUrl}
                                                    alt={avatar.label || `Avatar ${idx + 1}`}
                                                    className="w-full h-full object-cover"
                                                />
                                            </button>
                                            {avatar.label && (
                                                <span className={`text-xs font-semibold ${isSelected ? "text-mainBrandColor" : "text-gray-500"}`}>
                                                    {avatar.label}
                                                </span>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        <div className="flex items-center gap-4 w-full max-w-xs my-1">
                            <div className="h-px bg-gray-200 flex-1" />
                            <span className="text-xs text-gray-400 font-bold uppercase">OR</span>
                            <div className="h-px bg-gray-200 flex-1" />
                        </div>

                        <div className="flex flex-col items-center space-y-2">
                            <label className="cursor-pointer group flex flex-col items-center justify-center">
                                <div className={`w-16 h-16 rounded-full border-2 border-dashed flex items-center justify-center transition-all duration-300 ${
                                    preview || value instanceof File
                                        ? "border-emerald-500 bg-emerald-50 text-emerald-600"
                                        : "border-gray-300 bg-white hover:border-mainBrandColor hover:bg-orange-50/30 text-gray-400 hover:text-mainBrandColor"
                                }`}>
                                    {preview ? (
                                        <img
                                            src={preview}
                                            alt="Preview"
                                            className="w-full h-full rounded-full object-cover"
                                        />
                                    ) : (
                                        <Upload size={22} className="transition-transform group-hover:scale-110" />
                                    )}
                                </div>

                                <input
                                    type="file"
                                    accept="image/*"
                                    className="hidden"
                                    onChange={(e) => {
                                        const file = e.target.files?.[0];
                                        if (file) handleFileChange(file);
                                    }}
                                />
                            </label>

                            <div className="text-center">
                                <p className="text-xs font-bold text-gray-700">
                                    {preview || value instanceof File ? "Photo Selected!" : "Upload Pet Photo"}
                                </p>
                                <p className="text-[10px] text-gray-400 mt-0.5">
                                    PNG, JPG or JPEG
                                </p>
                            </div>
                        </div>
                    </div>
                )}

                {/* TYPE: TEXT */}
                {type === "text" && (
                    <TextInputComp
                        value={value ?? ""}
                        onChangeText={(text) => onChange?.(text)}
                        placeholder="Enter name"
                    />
                )}

                {/* TYPE: DROPDOWN (Click outside handles closing) */}
                {type === "dropdown" && (
                    <div ref={dropdownRef} className="relative max-w-lg mx-auto z-50">
                        <div className="relative">
                            <input
                                type="text"
                                value={searchTerm}
                                onChange={handleSearchInput}
                                onFocus={() => setIsDropdownOpen(true)}
                                placeholder="Type or search breed name..."
                                className="w-full px-4 py-3.5 border border-gray-300 rounded-2xl text-sm focus:outline-none focus:border-mainBrandColor focus:ring-2 focus:ring-mainBrandColor/10 pr-10 text-gray-700 font-medium"
                            />
                            <ChevronDown
                                size={18}
                                className="absolute right-3.5 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
                            />
                        </div>

                        {/* Searchable Options Dropdown Menu */}
                        {isDropdownOpen && searchableOptions.length > 0 && (
                            <div className="absolute left-0 right-0 top-full mt-2 bg-white border border-gray-200 rounded-2xl shadow-xl max-h-60 overflow-y-auto z-50 p-1 divide-y divide-gray-50">
                                {searchableOptions.map((item) => {
                                    const isSelected = value === item.label || value === item.id;
                                    return (
                                        <button
                                            key={item.id}
                                            type="button"
                                            onMouseDown={(e) => {
                                                e.preventDefault(); // Prevents input blur before selection completes
                                                handleSelectOption(item.label);
                                            }}
                                            className={`w-full text-left px-4 py-3 text-sm rounded-xl flex items-center justify-between transition-colors ${
                                                isSelected
                                                    ? "bg-mainBrandColor/10 text-mainBrandColor font-semibold"
                                                    : "hover:bg-gray-50 text-gray-700"
                                            }`}
                                        >
                                            <span>{item.label}</span>
                                            {isSelected && <Check size={16} className="text-mainBrandColor" />}
                                        </button>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* TYPE: RADIO */}
                {type === "radio" && (
                    <TabPills
                        options={
                            filteredOptions.map(item => ({
                                id: item.id,
                                label: item.label,
                                value: item.value || item.id,
                            }))
                        }
                        value={value || ""}
                        onChange={onChange || (() => { })}
                    />
                )}

                {/* Action Buttons */}
                <div className="mt-8 flex justify-center gap-4">
                    {currentStep > 1 && (
                        <CommonButton
                            text="Back"
                            variant="outline"
                            rounded="md"
                            disabled={disabled}
                            onClick={onBack}
                            className="!w-[25%] justify-center"
                        />
                    )}

                    <CommonButton
                        text={
                            disabled
                                ? "Saving..."
                                : currentStep === totalSteps
                                ? "Finish"
                                : "Continue"
                        }
                        variant="filled"
                        rounded="md"
                        disabled={!isAnswered() || disabled}
                        onClick={onContinue}
                        className={`${currentStep > 1 ? "!w-[25%]" : "!w-[50%]"} justify-center`}
                    />
                </div>
            </div>
        </div>
    );
};

export default QuestionCard;